import { broadcastInvalidation, isFromBroadcast } from "../crossTabSync";

// Manual mapping of mutation endpoints to their invalidated tags
// This mirrors the invalidatesTags configuration in each API file
const MUTATION_TAG_MAP: Record<
  string,
  (result: any, arg: any) => Array<{ type: string; id?: string }>
> = {
  // Request mutations
  createRequest: () => [{ type: "Requests", id: "LIST" }],
  updateRequest: (result, arg) => [
    { type: "Requests", id: "LIST" },
    { type: "Requests", id: arg?.id },
    { type: "MedicalRequests", id: arg?.id },
  ],
  bulkUpdateRequests: () => [{ type: "Requests", id: "LIST" }],
  deleteRequest: () => [{ type: "Requests", id: "LIST" }],

  // Medical request mutations
  createMedicalRequest: (result, arg) => [
    { type: "Requests", id: "LIST" },
    { type: "MedicalRequests", id: arg?.id },
  ],
  updateMedicalRequest: (result, arg) => [
    { type: "Requests", id: "LIST" },
    { type: "MedicalRequests", id: arg?.id },
  ],
  deleteMedicalRequest: (result, arg) => [
    { type: "Requests", id: "LIST" },
    { type: "Requests", id: arg?.id },
  ],

  // Patient mutations
  createPatient: () => [{ type: "Patients", id: "LIST" }],
  updatePatient: (result, arg) => [
    { type: "Patients", id: "LIST" },
    { type: "Patients", id: arg?.id },
  ],
  deletePatient: () => [{ type: "Patients", id: "LIST" }],

  // Provider mutations
  createProvider: () => [{ type: "Providers", id: "LIST" }],
  updateProvider: (result, arg) => [
    { type: "Providers", id: "LIST" },
    { type: "Providers", id: arg?.id },
  ],
  deleteProvider: () => [{ type: "Providers", id: "LIST" }],
  pushNoteToConnected: () => [{ type: "Providers" }],
  updateProviderConnection: () => [{ type: "Providers" }],
  deleteProviderConnection: () => [{ type: "Providers" }],

  // Firm mutations
  createFirm: () => [{ type: "Firms", id: "LIST" }],
  updateFirm: (result, arg) => [
    { type: "Firms", id: "LIST" },
    { type: "Firms", id: arg?.id },
  ],
  deleteFirm: () => [{ type: "Firms", id: "LIST" }],

  // User mutations
  createUser: () => [{ type: "Users", id: "LIST" }],
  updateUser: (result, arg) => [
    { type: "Users", id: "LIST" },
    { type: "Users", id: arg?.id },
    { type: "User" },
  ],
  deleteUser: () => [{ type: "Users", id: "LIST" }],

  // Patient form mutations
  createPatientForm: () => [{ type: "PatientForms", id: "LIST" }],
  updatePatientForm: (result, arg) => [
    { type: "PatientForms", id: "LIST" },
    { type: "PatientForms", id: arg?.id },
  ],
  deletePatientForm: () => [{ type: "PatientForms", id: "LIST" }],
};

// Middleware that broadcasts cache invalidations to other tabs
export const crossTabSyncMiddleware = () => (next: any) => (action: any) => {
  const result = next(action);

  // Early bail-out for non-relevant actions
  const actionType = action.type;
  if (
    actionType !== "api/executeMutation/fulfilled" &&
    actionType !== "api/invalidateTags"
  ) {
    return result;
  }

  // Don't broadcast if this came from another tab
  if (isFromBroadcast()) {
    return result;
  }

  // Handle mutation fulfilled actions
  if (actionType === "api/executeMutation/fulfilled") {
    const endpointName = action.meta?.arg?.endpointName;
    if (endpointName) {
      const tagFn = MUTATION_TAG_MAP[endpointName];
      if (tagFn) {
        const tags = tagFn(action.payload, action.meta?.arg?.originalArgs);
        if (tags.length > 0) {
          broadcastInvalidation(tags);
        }
      }
    }
  }

  // Handle manual invalidateTags calls
  if (actionType === "api/invalidateTags") {
    const tags = action.payload;
    if (tags && Array.isArray(tags) && tags.length > 0) {
      broadcastInvalidation(tags);
    }
  }

  return result;
};
